import { fechaCorta } from './fechas';

export const ESTADO_DISPONIBLE = 'disponible';
export const ESTADO_EN_PRUEBA = 'en_prueba';
export const ESTADO_VENCIDO = 'vencido';

export const EVENTO_MODULOS = 'sorby-ia:modulos';
export const TIPO_MODULOS_ACTUALIZADOS = 'modulos_actualizados';

// Lo que dice «Tu Sorby» al lado de cada módulo.
export const etiquetaEstadoModulo = (modulo) => {
  if (modulo?.estado === ESTADO_EN_PRUEBA) return `En prueba hasta ${fechaCorta(modulo.venceAt)}`.trim();
  if (modulo?.estado === ESTADO_VENCIDO) return 'Prueba vencida';
  return 'Disponible';
};

export const modulosParaMostrar = (modulos) => (Array.isArray(modulos) ? modulos.filter((m) => m?.clave && m?.nombre) : []);

// «Tu Sorby» solo cuenta lo que ya se probó; los módulos disponibles se ofrecen de a uno en el chat, no en una lista.
export const modulosConEstado = (modulos) => modulosParaMostrar(modulos).filter((m) => m.estado !== ESTADO_DISPONIBLE);

// Espejo del catálogo del backend (el front no lo recibe): mantener alineado a mano. Cada módulo prende estas entradas del menú; solo la primera lleva el vencimiento. `accion` es el permiso que ya
// gobierna esa pantalla en el menú completo: una persona sin él no ve la entrada aunque el módulo esté activo.
const MENU_POR_MODULO = {
  control_obra: [
    { id: 'control-obra', titulo: 'Control de Obra', accion: 'VER_CONTROL_OBRA', ruta: () => '/control-obra' },
  ],
  acopio_stock: [
    { id: 'acopio', titulo: 'Acopio', accion: 'CREAR_ACOPIO', ruta: (empresaId) => `/acopios?empresaId=${empresaId}` },
    { id: 'stock-materiales', titulo: 'Stock de materiales', accion: 'VER_STOCK_MATERIALES', ruta: (empresaId) => `/stockMateriales?empresaId=${empresaId}` },
  ],
};

const tituloConVencimiento = (titulo, venceAt) => `${titulo} · en prueba hasta ${fechaCorta(venceAt)}`;

// Módulo → entradas del menú. Solo los que están en prueba; al vencer o desactivar no queda nada.
export const itemsMenuModulos = (modulos, { empresaId, permisos = [] } = {}) =>
  modulosParaMostrar(modulos)
    .filter((m) => m.estado === ESTADO_EN_PRUEBA && MENU_POR_MODULO[m.clave])
    .flatMap((m) =>
      MENU_POR_MODULO[m.clave]
        .filter((entrada) => permisos.includes(entrada.accion))
        .map((entrada, pos) => ({
          id: entrada.id,
          title: pos === 0 ? tituloConVencimiento(entrada.titulo, m.venceAt) : entrada.titulo,
          path: entrada.ruta(empresaId),
        })),
    );

const sinQuery = (path) => String(path || '').split('?')[0];

// Menú completo (cuenta en onboarding): las pantallas del módulo ya salen por sus permisos; acá solo se les pone el vencimiento.
export const marcarModulosEnMenu = (groups, modulos, { empresaId, permisos = [] } = {}) => {
  const porPath = new Map(itemsMenuModulos(modulos, { empresaId, permisos }).map((it) => [sinQuery(it.path), it]));
  if (porPath.size === 0) return groups;
  return groups.map((g) => ({
    ...g,
    items: (g.items || []).map((it) => (porPath.has(sinQuery(it.path)) ? { ...it, title: porPath.get(sinQuery(it.path)).title } : it)),
  }));
};

// El chat avisa que cambió un módulo; el menú y «Tu Sorby» vuelven a leer el estado.
export const avisarCambioDeModulos = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENTO_MODULOS));
};

// true si el último mensaje de la IA trae el aviso de que se activó o desactivó un módulo.
export const huboCambioDeModulos = (mensajes) => {
  const ultimo = Array.isArray(mensajes) ? mensajes[mensajes.length - 1] : null;
  return !!(ultimo?.role === 'assistant' && Array.isArray(ultimo.actions) && ultimo.actions.some((a) => a?.type === TIPO_MODULOS_ACTUALIZADOS));
};

const DIA_MS = 24 * 60 * 60 * 1000;
const AVISO_DIAS = 3;

// Lo que el saludo le dice a la persona: una prueba por vencer (3 días o menos) o una que ya terminó.
export const avisosDeModulos = (modulos, ahora = new Date()) =>
  modulosParaMostrar(modulos).flatMap((m) => {
    if (m.estado === ESTADO_VENCIDO) return [`La prueba de ${m.nombre} terminó. Tus datos siguen guardados: si querés seguir, pedime una reunión.`];
    const falta = new Date(m.venceAt) - ahora;
    if (m.estado !== ESTADO_EN_PRUEBA || Number.isNaN(falta) || falta > AVISO_DIAS * DIA_MS) return [];
    const dias = Math.max(1, Math.ceil(falta / DIA_MS));
    return [`Tu prueba de ${m.nombre} vence ${dias === 1 ? 'mañana' : `en ${dias} días`}.`];
  });
